import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { formatDateTimeShort } from '../../i18n/format';

const humanizeSlug = (slug) =>
    slug
        .split(/[_-]/)
        .map(part => {
            if (part.length === 0) return part;
            const capitalized = part[0].toUpperCase() + part.slice(1);
            // Brand styling: fasttrak -> FastTrak, gyrotrak -> GyroTrak, etc.
            return capitalized.replace(/trak$/i, 'Trak');
        })
        .join(' ');

const formatSize = (bytes) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
};

// The manuals themselves are server-rendered HTML and stay in English; this is only the list.
export const Manuals = () => {
    const { t } = useTranslation();
    const [manuals, setManuals] = useState(null);
    const [error, setError] = useState(null);

    useEffect(() => {
        let cancelled = false;
        fetch('api/manuals')
            .then(r => r.ok ? r.json() : Promise.reject(new Error(`HTTP ${r.status}`)))
            .then(data => { if (!cancelled) setManuals(data); })
            .catch(err => { if (!cancelled) setError(err.message); });
        return () => { cancelled = true; };
    }, []);

    return (
        <div className="container py-4">
            <div className="d-flex align-items-baseline mb-3">
                <h2 className="mb-0">{t('nav.manuals')}</h2>
                <span className="text-muted ms-3 small">
                    {manuals ? t('downloads.manualCount', { number: manuals.length }) : ''}
                </span>
            </div>
            <p className="text-muted">
                {t('downloads.manualsIntro')}
            </p>

            {error && (
                <div className="alert alert-danger" role="alert">
                    {t('downloads.manualListFailed', { error })}
                </div>
            )}

            {!manuals && !error && (
                <p><em>{t('downloads.loading')}</em></p>
            )}

            {manuals && manuals.length === 0 && (
                <p className="text-muted"><em>{t('downloads.noManuals')}</em></p>
            )}

            {manuals && manuals.length > 0 && (
                <div className="row g-3">
                    {manuals.map(m => (
                        <div className="col-12 col-md-6 col-lg-4" key={m.slug}>
                            <a href={`/manuals/${m.slug}`} target="_blank" rel="noopener noreferrer" className="text-decoration-none text-reset">
                                <div className="card card-clickable h-100 shadow-sm">
                                    <div className="card-body">
                                        <h5 className="card-title mb-1">{humanizeSlug(m.slug)}</h5>
                                        <div className="small mb-2" style={{ opacity: 0.75, fontFamily: 'monospace' }}>
                                            {m.slug}
                                        </div>
                                        <div className="d-flex justify-content-between small" style={{ opacity: 0.85 }}>
                                            <span>{formatDateTimeShort(m.lastModified)}</span>
                                            <span>{formatSize(m.sizeBytes)}</span>
                                        </div>
                                    </div>
                                </div>
                            </a>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};
